import { useCallback, useContext } from "react";

import { authTokenServiceTokenPostV2 } from "@btnet/services-btnet/handlers/auth-token/auth-token";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

interface ICachedToken {
    token: string | null;
    expiresAt: number;
    pending: Promise<string> | null;
}

/**
 * Shared across hook instances: the dialog remounts on every open. Keyed by
 * account (globalUserId): a builder switch keeps the page loaded, and a token
 * is scoped to the builder it was minted for.
 */
const tokenCache = new Map<string, ICachedToken>();

export function resetAuthTokenCacheForTests(): void {
    tokenCache.clear();
}

function cacheEntry(key: string): ICachedToken {
    let entry = tokenCache.get(key);
    if (!entry) {
        entry = { token: null, expiresAt: 0, pending: null };
        tokenCache.set(key, entry);
    }
    return entry;
}

/**
 * JWT auth tokens for external BT services. Lazily minted on first use, cached,
 * and refreshed shortly before expiry.
 *
 * Usage:
 *   const { getToken } = useAuthToken("https://search.buildertrend.net/");
 *   const token = await getToken();
 */
export function useAuthToken(audience: string) {
    const userInfo = useContext(UserInfoContext);
    const cacheKey = `${audience}|${userInfo?.globalUserId ?? ""}`;

    const getToken = useCallback(async (): Promise<string> => {
        const entry = cacheEntry(cacheKey);
        if (entry.token && Date.now() < entry.expiresAt) {
            return entry.token;
        }
        if (entry.pending) {
            return entry.pending;
        }

        const promise = authTokenServiceTokenPostV2({ audience }).then((response) => {
            const accessToken = response.data.access_token;
            if (!accessToken) {
                throw new Error("Auth token response missing access_token");
            }
            if (entry.pending === promise) {
                entry.token = accessToken;
                entry.expiresAt =
                    Date.now() + (response.data.expires_in ?? 0) * 1000 - REFRESH_BUFFER_MS;
                entry.pending = null;
            }
            return accessToken;
        });
        entry.pending = promise;
        // A rejected promise left here would be every later caller's answer.
        promise.catch(() => {
            if (entry.pending === promise) {
                entry.pending = null;
            }
        });
        return promise;
    }, [audience, cacheKey]);

    const clearToken = useCallback(() => {
        tokenCache.delete(cacheKey);
    }, [cacheKey]);

    return { getToken, clearToken };
}
